import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import axios from '../../config/axios';
import ModalConfirm from '../../components/ModalConfirm';
import ModalResult from '../../components/ModalResult';
import {
  getCategoryLabel,
  getCategoryColor,
  RESERVABLE_CATEGORIES,
} from '../../utils/reservableHelpers';

const ReservablesManagement = () => {
  const navigate = useNavigate();
  const [reservables, setReservables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    location: '',
    isActive: '',
  });
  const [confirmModal, setConfirmModal] = useState({ open: false, reservable: null });
  const [resultModal, setResultModal] = useState({ open: false, message: '', severity: 'info' });

  const fetchReservables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/reservables/admin/all');
      setReservables(response.data);
    } catch (err) {
      setError('Errore nel caricamento delle risorse');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservables();
  }, []);

  const filteredReservables = useMemo(() => {
    let list = [...reservables];
    const searchLower = filters.search.toLowerCase().trim();

    if (searchLower) {
      list = list.filter(
        (r) =>
          r.name?.toLowerCase().includes(searchLower) ||
          r.description?.toLowerCase().includes(searchLower)
      );
    }
    if (filters.category) {
      list = list.filter((r) => r.category === filters.category);
    }
    if (filters.location) {
      const loc = filters.location.toLowerCase();
      list = list.filter((r) => r.location?.toLowerCase().includes(loc));
    }
    if (filters.isActive === 'true') {
      list = list.filter((r) => r.isActive);
    } else if (filters.isActive === 'false') {
      list = list.filter((r) => !r.isActive);
    }

    return list;
  }, [reservables, filters]);

  const handleDelete = async () => {
    if (!confirmModal.reservable) return;
    try {
      await axios.delete(`/api/reservables/${confirmModal.reservable._id}`);
      setResultModal({
        open: true,
        message: 'Risorsa eliminata con successo',
        severity: 'success',
      });
      fetchReservables();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Impossibile eliminare la risorsa';
      setResultModal({ open: true, message, severity: 'error' });
    } finally {
      setConfirmModal({ open: false, reservable: null });
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => navigate(`/manage-reservables/${params.row._id}`)}
          sx={{ textTransform: 'none', justifyContent: 'flex-start', p: 0 }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: 'category',
      headerName: 'Categoria',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={getCategoryLabel(params.value)}
          color={getCategoryColor(params.value)}
          size="small"
        />
      ),
    },
    { field: 'location', headerName: 'Location', flex: 1, minWidth: 140 },
    {
      field: 'maxConcurrentAccess',
      headerName: 'Accessi',
      flex: 0.5,
      minWidth: 90,
    },
    {
      field: 'costPerHour',
      headerName: '€/ora',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => `${params.value}€`,
    },
    {
      field: 'isActive',
      headerName: 'Stato',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Attiva' : 'Inattiva'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      flex: 0.6,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => navigate(`/manage-reservables/${params.row._id}`)}
          >
            <Edit />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => setConfirmModal({ open: true, reservable: params.row })}
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Gestisci Risorse
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Crea, modifica ed elimina le risorse prenotabili
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/manage-reservables/create')}
        >
          Nuova risorsa
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="Cerca nome o descrizione"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            sx={{ minWidth: 220, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={filters.category}
              label="Categoria"
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <MenuItem value="">Tutte</MenuItem>
              {RESERVABLE_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Location"
            value={filters.location}
            onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            sx={{ minWidth: 160 }}
          />
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Stato</InputLabel>
            <Select
              value={filters.isActive}
              label="Stato"
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
            >
              <MenuItem value="">Tutte</MenuItem>
              <MenuItem value="true">Attive</MenuItem>
              <MenuItem value="false">Inattive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <DataGrid
        rows={filteredReservables}
        columns={columns}
        getRowId={(row) => row._id}
        autoHeight
        disableRowSelectionOnClick
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        sx={{ backgroundColor: 'background.paper' }}
      />

      <ModalConfirm
        open={confirmModal.open}
        title="Elimina risorsa"
        message={`Eliminare definitivamente "${confirmModal.reservable?.name}"? L'operazione è consentita solo senza prenotazioni attive.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmModal({ open: false, reservable: null })}
      />
      <ModalResult
        open={resultModal.open}
        onClose={() => setResultModal({ open: false, message: '', severity: 'info' })}
        message={resultModal.message}
        severity={resultModal.severity}
      />
    </Container>
  );
};

export default ReservablesManagement;
